import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./Detail.css";
import { createPortal } from "react-dom";
import { useAppContext } from "../../contexts/AppContext";
import parse from "html-react-parser";
import Home from "../Home/Home";
import { isImage, isVideo } from "../MediaFilter";
import thumbnailFallback from "../../assets/thumbnail.jpg";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Loading from "../Loading/Loading";

function Detail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mainVideoRef = useRef(null);
  const {
    translate,
    selectedData,
    fullData,
    backendUrl,
    updateSelectedData,
    updateFullData,
    updateSelectedTitle,
    selectedTitle,
  } = useAppContext();

  const [activeMedia, setActiveMedia] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fullscreenMedia, setFullscreenMedia] = useState(null);

  // Determine route type based on URL
  const routeInfo = useMemo(() => {
    if (location.pathname.includes("/about/")) return { route: "/AboutList", title: "About", path: "/about" };
    if (location.pathname.includes("/services/")) return { route: "/ServiceList", title: "Services", path: "/services" };
    if (location.pathname.includes("/doctors/")) return { route: "/ProductList", title: "Doctors", path: "/doctors" };
    return null;
  }, [location.pathname]);

  // Handle data fetching and synchronization
  useEffect(() => {
    if (!routeInfo || !id) return;

    const itemId = parseInt(id);

    // 1. Check if we already have the data in context
    const isSameCategory = selectedTitle === routeInfo.title;
    const hasData = Array.isArray(fullData) && fullData.length > 0;

    if (isSameCategory && hasData) {
      // Smooth update: just find the right item
      const item = fullData.find(i => i.id === itemId);
      if (item && (!selectedData || selectedData.id !== item.id)) {
        updateSelectedData(item);
      }
    } else {
      // 2. Refresh or category switch: fetch data
      const fetchData = async () => {
        // Only show loading if we don't have ANY data yet for this category
        if (!hasData || !isSameCategory) setLoading(true);

        try {
          const response = await axios.get(`${backendUrl}${routeInfo.route}`);
          if (Array.isArray(response.data)) {
            updateFullData(response.data);
            updateSelectedTitle(routeInfo.title);
            const selected = response.data.find(item => item.id === itemId);
            if (selected) {
              updateSelectedData(selected);
            }
          }
        } catch (error) {
          console.error("Error fetching detail data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [id, routeInfo, backendUrl, fullData, selectedTitle, selectedData, updateFullData, updateSelectedData, updateSelectedTitle]);

  // Update active media and index when selected data changes
  useEffect(() => {
    if (selectedData?.images?.length > 0) {
      setActiveMedia(selectedData.images[0]);
    } else {
      setActiveMedia(null);
    }

    if (Array.isArray(fullData) && selectedData) {
      const index = fullData.findIndex(item => item.id === selectedData.id);
      if (index !== -1) setSelectedIndex(index);
    }
  }, [selectedData, fullData]);

  // Pause main video when modal opens
  useEffect(() => {
    if (fullscreenMedia && mainVideoRef.current) {
      mainVideoRef.current.pause();
    }
  }, [fullscreenMedia]);

  const handleSidebarClick = (item) => {
    if (routeInfo) {
      navigate(`${routeInfo.path}/${item.id}`);
    }
  };

  const nextTopic = () => {
    if (Array.isArray(fullData) && selectedIndex < fullData.length - 1) {
      handleSidebarClick(fullData[selectedIndex + 1]);
    }
  };

  const prevTopic = () => {
    if (Array.isArray(fullData) && selectedIndex > 0) {
      handleSidebarClick(fullData[selectedIndex - 1]);
    }
  };

  if (loading) return <Loading />;
  // Fallback: If we have an ID but no data yet, wait for effect. 
  // If we have no ID and no data, show Home.
  if (!id && (!fullData || fullData.length === 0)) return <Home />;

  return (
    <div className="detail-page-wrapper" id="details">
      {fullscreenMedia && createPortal(
        <div className="fullscreen-overlay" onClick={() => setFullscreenMedia(null)}>
          <button className="close-btn" onClick={() => setFullscreenMedia(null)}>
            <i className="bi bi-x-lg"></i>
          </button>
          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {isImage(fullscreenMedia) ? (
              <img src={fullscreenMedia} alt="Fullscreen" />
            ) : isVideo(fullscreenMedia) ? (
              <video src={fullscreenMedia} controls autoPlay />
            ) : null}
          </div>
        </div>,
        document.body
      )}

      <div className="detail-layout">
        {/* Sidebar Menu */}
        <aside className="detail-sidebar">
          <div className="sidebar-header">
            <h4>{parse(translate(selectedTitle || "Categories"))}</h4>
            <div className="title-accent"></div>
          </div>
          <div className="sidebar-links">
            {Array.isArray(fullData) && fullData.map((item, index) => (
              <button
                key={item.id || index}
                className={`sidebar-link ${selectedData?.id === item.id ? "active" : ""}`}
                onClick={() => handleSidebarClick(item)}
              >
                {parse(translate(item.title || "No Title"))}
              </button>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main className="detail-main">
          <div className="content-card">
            <div className="text-content">
              <h1 className="content-title">
                {parse(translate(selectedData?.title || "No Title"))}
              </h1>
              <div className="content-divider"></div>
              <div className="content-body">
                {parse(translate(selectedData?.content || ""))}
              </div>
            </div>

            {/* Gallery Section */}
            {selectedData?.images?.length > 0 && (
              <div className="premium-gallery">
                <div
                  className="featured-media pointer"
                  onClick={() => setFullscreenMedia(`${backendUrl}/${activeMedia}`)}
                >
                  {isImage(`${backendUrl}/${activeMedia}`) ? (
                    <img
                      src={`${backendUrl}/${activeMedia}`}
                      alt="Featured"
                      className="featured-img animate__animated animate__fadeIn"
                    />
                  ) : isVideo(`${backendUrl}/${activeMedia}`) ? (
                    <video
                      ref={mainVideoRef}
                      src={`${backendUrl}/${activeMedia}`}
                      controls
                      className="featured-video"
                    />
                  ) : null}
                </div>

                {selectedData.images.length > 1 && (
                  <div className="thumbnail-strip">
                    {selectedData.images.map((img, idx) => (
                      <div
                        key={idx}
                        className={`thumbnail-item ${activeMedia === img ? "active" : ""}`}
                        onClick={() => setActiveMedia(img)}
                      >
                        {isImage(`${backendUrl}/${img}`) ? (
                          <img src={`${backendUrl}/${img}`} alt={`Thumbnail ${idx}`} />
                        ) : isVideo(`${backendUrl}/${img}`) ? (
                          <img src={thumbnailFallback} alt={`Thumbnail ${idx}`} />
                        ) : (
                          <div className="thumbnail-placeholder">
                            <i className="bi bi-file-earmark-play"></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            <div className="pagination-footer">
              <button
                className="pag-btn"
                onClick={prevTopic}
                disabled={selectedIndex === 0}
              >
                <i className="bi bi-chevron-left"></i> {parse(translate("Previous"))}
              </button>
              <button
                className="pag-btn"
                onClick={nextTopic}
                disabled={selectedIndex === (fullData?.length || 0) - 1}
              >
                {parse(translate("Next"))} <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Detail;
